<template>
  <div class="flex flex-col h-full bg-[#1e1e1e] border-t lg:border-t-0 border-slate-800">
    <!-- Editor Header Toolbar -->
    <div class="bg-slate-900 border-b border-slate-800 px-3 py-1.5 flex items-center justify-between select-none">
      <div class="flex items-center space-x-3">
        <span class="text-xs font-semibold text-slate-300 flex items-center font-mono">
          <i class="pi pi-code text-unibo-red mr-1.5"></i>
          python_lab.py
        </span>
        <span class="text-[10px] text-slate-500 font-mono hidden sm:inline">
          (Shift+Enter to Run)
        </span>
      </div>

      <div class="flex items-center space-x-2">
        <!-- Notebook cell mode toggle -->
        <button 
          @click="toggleCellMode"
          :class="[
            'px-2 py-1 rounded text-xs font-mono transition-colors flex items-center space-x-1',
            isMultiCell ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/30' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
          ]"
          v-tooltip.bottom="'Toggle Multi-Cell Notebook Mode'"
        >
          <i class="pi pi-th-large text-xs"></i>
          <span class="hidden md:inline">{{ isMultiCell ? 'Multi-Cell' : 'Single-Cell' }}</span>
        </button>

        <!-- Restore Snapshots -->
        <button 
          @click="$emit('open-snapshots')"
          class="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs transition-colors flex items-center space-x-1"
          v-tooltip.bottom="'View & Restore History Snapshots'"
        >
          <i class="pi pi-history text-xs text-amber-400"></i>
          <span class="hidden sm:inline">History</span>
        </button>

        <!-- Clear Code -->
        <button 
          @click="$emit('reset-starter')"
          class="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs transition-colors flex items-center space-x-1"
          v-tooltip.bottom="'Reset to Starter Template'"
        >
          <i class="pi pi-refresh text-xs text-rose-400"></i>
          <span class="hidden sm:inline">Reset</span>
        </button>

        <!-- Run Code Button -->
        <button 
          @click="$emit('run')"
          :disabled="isRunning"
          class="px-3.5 py-1 bg-unibo-red hover:bg-unibo-darkRed disabled:opacity-50 text-white font-medium rounded text-xs shadow-md transition-all flex items-center space-x-1.5 active:scale-95"
        >
          <i :class="isRunning ? 'pi pi-spin pi-spinner text-xs' : 'pi pi-play text-xs'"></i>
          <span>{{ isRunning ? 'Executing...' : 'Run Code' }}</span>
        </button>
      </div>
    </div>

    <!-- CodeMirror Editor Container -->
    <div ref="editorContainer" class="flex-1 overflow-hidden font-mono text-sm"></div>

    <!-- Editor Status Footer -->
    <div class="bg-slate-900 border-t border-slate-800 px-3 py-1 text-[11px] font-mono text-slate-500 flex items-center justify-between select-none">
      <span>UTF-8 • Python 3 (Pyodide WebAssembly)</span>
      <div class="flex items-center space-x-3">
        <span v-if="lastSaved" class="text-slate-400">Autosaved {{ lastSaved }}</span>
        <span>Lines: {{ lineCount }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { EditorState, Prec } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';

const props = defineProps<{
  modelValue: string;
  isRunning: boolean;
  theme: 'dark' | 'light';
  lastSaved?: string;
}>();

const emit = defineEmits(['update:modelValue', 'run', 'save', 'reset-starter', 'open-snapshots']);

const editorContainer = ref<HTMLDivElement | null>(null);
const lineCount = ref(1);
const isMultiCell = ref(false);

let editorView: EditorView | null = null;

function toggleCellMode() {
  isMultiCell.value = !isMultiCell.value;
}

onMounted(() => {
  if (!editorContainer.value) return;

  const startState = EditorState.create({
    doc: props.modelValue,
    extensions: [
      lineNumbers(),
      highlightActiveLineGutter(),
      history(),
      python(),
      oneDark,
      // Prec.highest so Shift-Enter runs code instead of defaultKeymap's
      // { key: "Enter", shift: insertNewlineAndIndent }.
      Prec.highest(
        keymap.of([
          {
            key: 'Shift-Enter',
            run: () => {
              emit('run');
              return true;
            },
          },
          {
            key: 'Mod-s',
            run: () => {
              emit('save');
              return true;
            },
          },
        ])
      ),
      keymap.of([
        indentWithTab,
        ...defaultKeymap,
        ...historyKeymap,
      ]),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          const newCode = update.state.doc.toString();
          lineCount.value = update.state.doc.lines;
          emit('update:modelValue', newCode);
        }
      }),
      EditorView.theme({
        '&': { height: '100%', backgroundColor: '#0f172a' },
        '.cm-scroller': { overflow: 'auto' },
        '.cm-gutters': { backgroundColor: '#090d16', borderColor: '#1e293b', color: '#64748b' },
        '.cm-activeLine': { backgroundColor: '#1e293b50' },
      }),
      EditorState.tabSize.of(4),
    ],
  });

  editorView = new EditorView({
    state: startState,
    parent: editorContainer.value,
  });

  lineCount.value = startState.doc.lines;
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (editorView && newValue !== editorView.state.doc.toString()) {
      editorView.dispatch({
        changes: { from: 0, to: editorView.state.doc.length, insert: newValue },
      });
      lineCount.value = editorView.state.doc.lines;
    }
  }
);

onBeforeUnmount(() => {
  if (editorView) {
    editorView.destroy();
    editorView = null;
  }
});
</script>
